import React, { useContext, useState } from "react";
import UnsavedChangesDialog from "../UnsavedChangesDialog";
import useStyles from "./styles";

export interface ConfirmationContextModel {
  openConfirmation: () => void;
  setIsLoading: (isLoading: boolean) => void;
  confirmationRequired: boolean;
  setConfirmationRequired: (confirmationRequired: boolean) => void;
  confirmationDisabled: boolean;
  setConfirmationDisabled: (confirmationDisabled: boolean) => void;
  setHandleConfirm: (callback: () => void) => void;
  setHandleDiscard: (callback: () => void) => void;
}

export const ConfirmationContext =
  React.createContext<ConfirmationContextModel>({
    openConfirmation: () => {
      return null;
    },
    setIsLoading: (isLoading: boolean) => {
      return null;
    },
    confirmationRequired: false,
    setConfirmationRequired: (confirmationRequired: boolean) => {
      return null;
    },
    confirmationDisabled: false,
    setConfirmationDisabled: (confirmationDisabled: boolean) => {
      return null;
    },
    setHandleConfirm: (callback: () => void) => {
      return null;
    },
    setHandleDiscard: (callback: () => void) => {
      return null;
    }
  });

export const useConfirmationContext = () => useContext(ConfirmationContext);

export const ConfirmationContextProvider = props => {
  const { children } = props;
  const classes = useStyles();

  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] =
    useState<boolean>(false);
  const [confirmationRequired, setConfirmationRequired] =
    useState<boolean>(false);
  const [confirmationDisabled, setConfirmationDisabled] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [exitedCallback, setExitedCallback] = useState<() => void>();

  const [handleConfirm, setHandleConfirm] = useState<() => void>();
  const [handleDiscard, setHandleDiscard] = useState<() => void>();

  const handleCancel = () => {
    setIsUnsavedChangesDialogOpen(false);
  };

  const openConfirmation = () => {
    setIsUnsavedChangesDialogOpen(true);
  };

  const handleConfirmClick = () => {
    handleConfirm && handleConfirm();
    setConfirmationRequired(false);
    setIsUnsavedChangesDialogOpen(false);
  };

  const handleDiscardClick = () => {
    handleDiscard && handleDiscard();
    setIsUnsavedChangesDialogOpen(false);
    setConfirmationRequired(false);
    setExitedCallback(() => () => {
      setConfirmationDisabled(false);
    });
  };

  const handleExited = () => {
    exitedCallback && exitedCallback();
    setExitedCallback(null);
  };

  return (
    <ConfirmationContext.Provider
      value={{
        setIsLoading,
        openConfirmation,
        confirmationRequired,
        setConfirmationRequired,
        confirmationDisabled,
        setConfirmationDisabled,
        setHandleConfirm,
        setHandleDiscard
      }}
    >
      {children}
      <UnsavedChangesDialog
        isLoading={isLoading}
        onSave={handleConfirmClick}
        onCancel={handleCancel}
        onDiscard={handleDiscardClick}
        open={isUnsavedChangesDialogOpen}
        content={
          <span className={classes.unsavedChangesDialogContent}>
            You have unsaved changes, would you like to{" "}
            {confirmationDisabled ? "continue" : "keep"} or discard these
            changes?
          </span>
        }
        isSaveDisabled={confirmationDisabled}
        TransitionProps={{
          onExited: handleExited
        }}
      />
    </ConfirmationContext.Provider>
  );
};
